import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { selector } from "../../api";
import { boundaryCRUD } from "../../actions/boundary";

/**
 * Scope-aware location cascade for the Building forms.
 *
 * Hierarchy: Region(21) -> District(22) -> Ward -> Shehia(23) -> Zone(24) -> Street.
 * The building attaches to the SHEHIA (boundary_id). Zone & Street are captured below it.
 *
 * Scope:
 *   - Admin/staff  -> full cascade (Region down).
 *   - Shehia user  -> Shehia locked to user.boundary_id; must pick Zone -> Street.
 *   (Region/District users fall into the admin-like path but start from their own boundary's
 *    children; simplest correct behaviour is: staff = full, non-staff Shehia = locked Shehia.)
 *
 * Emits onChange({ boundaryId, zoneId, streetId }).
 * `seed` (optional) = { boundaryId, zoneId, streetId } to pre-fill on Edit.
 */
export const BuildingLocationCascade = ({ onChange, seed }) => {
  const user = selector.User();
  const isStaff = !!user?.is_staff;
  const isShehiaUser = !isStaff && user?.admin_access === "Shehia" && user?.boundary_id;

  // Option lists
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [shehias, setShehias] = useState([]);
  const [zones, setZones] = useState([]);
  const [streets, setStreets] = useState([]);

  // Selections
  const [region, setRegion] = useState(null);
  const [district, setDistrict] = useState(null);
  const [shehia, setShehia] = useState(isShehiaUser ? user.boundary_id : null);
  const [zone, setZone] = useState(seed?.zoneId || null);
  const [street, setStreet] = useState(seed?.streetId || null);

  const isType = (b, id) => (b.dictionary_boundary_type?.id || b.dictionary_boundary_type) === id;

  // Admin: load Regions. Shehia user: load Zones/Streets straight from their Shehia.
  useEffect(() => {
    if (isStaff) {
      boundaryCRUD.loadBoundary(1000, 0, "Region", "")
        .then((r) => setRegions(r.data.results)).catch(() => {});
    } else if (isShehiaUser) {
      loadChildrenOfShehia(user.boundary_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cascade loaders (ADMIN path only — never reset a Shehia user's locked Shehia).
  useEffect(() => {
    if (!isStaff) return;
    setDistrict(null); setShehia(null); setDistricts([]); setShehias([]); resetBelowShehia();
    if (region) {
      boundaryCRUD.loadBoundaryByParent(region)
        .then((r) => setDistricts((r.data || []).filter((b) => isType(b, 22))))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  useEffect(() => {
    if (!isStaff) return;
    setShehia(null); setShehias([]); resetBelowShehia();
    if (district) {
      // Children of a District include Wards AND Shehias; we want Shehias (23) here.
      boundaryCRUD.loadBoundaryByParent(district)
        .then((r) => setShehias((r.data || []).filter((b) => isType(b, 23))))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [district]);

  // When a Shehia is chosen (admin path), load its Zones & Streets.
  useEffect(() => {
    if (shehia && isStaff) loadChildrenOfShehia(shehia);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shehia]);

  // Match a child's boundary type by NAME (robust against dictionary load timing).
  const typeName = (b) =>
    (b.dictionary_boundary_type?.dictionary_item_name_en || "").toLowerCase();

  function loadChildrenOfShehia(shehiaId) {
    resetBelowShehia();
    boundaryCRUD.loadBoundaryByParent(shehiaId).then((r) => {
      const children = r.data || [];
      setZones(children.filter((b) => typeName(b) === "zone"));
      setStreets(children.filter((b) => ["street", "road"].includes(typeName(b))));
    }).catch(() => {});
  }

  function resetBelowShehia() {
    setZone(null); setStreet(null); setZones([]); setStreets([]);
  }

  // The selected Shehia's official code (ShehiaNumber) for read-only display.
  const shehiaCode = useMemo(() => {
    if (isShehiaUser) return user?.boundary_code || user?.boundary_name || "";
    const s = shehias.find((b) => b.id === shehia);
    return s ? (s.boundary_code || "") : "";
  }, [isShehiaUser, shehias, shehia, user]);

  // Emit upward whenever the effective selection changes.
  useEffect(() => {
    if (onChange) onChange({ boundaryId: shehia || null, zoneId: zone || null, streetId: street || null, shehiaCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shehia, zone, street, shehiaCode]);

  const opt = (arr) => arr.map((b) => ({ value: b.id, label: b.boundary_name }));
  const cur = (arr, id) => (id ? opt(arr).find((o) => o.value === id) || null : null);

  return (
    <div className="row g-3 mb-2">
      {isStaff && (
        <>
          <div className="col-md-4">
            <label className="form-label">Region</label>
            <Select options={opt(regions)} value={cur(regions, region)} placeholder="Select region"
              onChange={(e) => setRegion(e?.value || null)} isClearable isSearchable />
          </div>
          <div className="col-md-4">
            <label className="form-label">District</label>
            <Select options={opt(districts)} value={cur(districts, district)} placeholder="Select district"
              onChange={(e) => setDistrict(e?.value || null)} isDisabled={!region} isClearable isSearchable />
          </div>
          <div className="col-md-4">
            <label className="form-label">Shehia <span className="text-danger">*</span></label>
            <Select options={opt(shehias)} value={cur(shehias, shehia)} placeholder="Select shehia"
              onChange={(e) => setShehia(e?.value || null)} isDisabled={!district} isClearable isSearchable />
          </div>
        </>
      )}

      {isShehiaUser && (
        <div className="col-md-4">
          <label className="form-label">
            Shehia <i className="ri-lock-2-line text-muted ms-1" title="Locked to your Shehia"></i>
          </label>
          <input
            className="form-control bg-light text-muted"
            value={user.boundary_name || "My Shehia"}
            readOnly
          />
        </div>
      )}

      <div className="col-md-4">
        <label className="form-label">Zone <span className="text-danger">*</span></label>
        <Select options={opt(zones)} value={cur(zones, zone)} placeholder="Select zone"
          onChange={(e) => { setZone(e?.value || null); setStreet(null); }}
          isDisabled={!shehia} isClearable isSearchable />
      </div>
      <div className="col-md-4">
        <label className="form-label">Street / Road <span className="text-muted small">(optional)</span></label>
        <Select options={opt(streets)} value={cur(streets, street)} placeholder="Select street"
          onChange={(e) => setStreet(e?.value || null)} isDisabled={!shehia} isClearable isSearchable />
      </div>
    </div>
  );
};

export default BuildingLocationCascade;
